import request from '@/utils/request'

// Read-only MCP access for LLM clients.
// Backend: /api/v2/admin/mcp-* (request baseURL already includes /api/v2).

export type PiiMode = 'inherit' | 'on' | 'off'

export interface McpKey {
  id: number
  userId: string
  email: string | null
  keyPrefix: string
  label: string
  environment: string
  // 'inherit' follows the panel default; 'on'/'off' pin this one key.
  piiMode: PiiMode
  // What the mode resolves to right now, worked out on the server so the list
  // and the server cannot disagree about who can read personal data.
  effectivePii: boolean
  isActive: boolean
  expiresAt: string
  expired: boolean
  lastUsedAt: string | null
  revokedAt: string | null
  revokedReason: string | null
  createdAt: string
  callCount: number
}

export interface McpCall {
  id: number
  userId: string | null
  email: string | null
  keyPrefix: string | null
  method: string | null
  toolName: string | null
  arguments: any
  // What the request was about - an address, a serial, a search term - lifted
  // out of the arguments so the log can be scanned without opening each row.
  subject: string | null
  // false means the key itself did not resolve, or the tool was refused.
  authorized: boolean
  ok: boolean
  error: string | null
  rowCount: number | null
  piiRevealed: boolean
  durationMs: number | null
  ip: string | null
  userAgent: string | null
  clientName: string | null
  clientVersion: string | null
  createdAt: string
}

export interface McpCallTotals {
  rejected: number
  failed: number
  withPii: number
  total: number
}

export const getMcpKeys = () => request({ url: '/admin/mcp-keys', method: 'get' })

export const createMcpKey = (data: {
  email?: string
  userId?: string
  label: string
  expiresInDays?: number
  piiMode?: PiiMode
}) => request({ url: '/admin/mcp-keys', method: 'post', data })

export const setMcpKeyPii = (id: number, piiMode: PiiMode) =>
  request({ url: `/admin/mcp-keys/${id}/pii`, method: 'post', data: { piiMode } })

export const resetMcpKeysPii = () =>
  request({ url: '/admin/mcp-keys/reset-pii', method: 'post' })

export const revokeMcpKey = (id: number, reason?: string) =>
  request({ url: `/admin/mcp-keys/${id}/revoke`, method: 'post', data: { reason } })

export const getMcpCalls = (params?: {
  limit?: number
  userId?: string
  toolName?: string
  subject?: string
  onlyProblems?: boolean
}) => request({ url: '/admin/mcp-calls', method: 'get', params })

export const getMcpSettings = () => request({ url: '/admin/mcp-settings', method: 'get' })

export const setMcpSettings = (piiEnabled: boolean) =>
  request({ url: '/admin/mcp-settings', method: 'put', data: { piiEnabled } })
